import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useAuth } from '@/app/auth';
import { useFlightAccess } from '@/app/flightAccess';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import { describeAccess, useAccessNames } from '@/lib/flightAccess';
import { NAME_MAX_LENGTH } from '@/types/user';
import type { UserInfo, UserProfileCard } from '@/types/user';
import { authenticate } from '@/features/auth/api';
import { editMe } from './api';
import { attachErrors, PasswordReveal, Readout } from './form';
import { useProfile } from './useProfile';
import styles from './ProfilePage.module.css';

/**
 * Your own account.
 *
 * Open to every signed-in role, and behind `POST /user/me` — which is a **different
 * shape** from the endpoint the administrator screen uses. `UserProfileCard` has no
 * `role`, no `isActive` and no `username`: raising your own privileges here is not
 * refused, it is unrepresentable. That is why this is its own component rather than
 * the admin panel behind a flag — a shared form would be one `canEditRole` prop away
 * from undoing the guarantee.
 *
 * The login and the role are still SHOWN, as plain values. An account is worth
 * understanding even where it is not yours to change, and stating the role is what
 * explains why a screen elsewhere is refused. What you can change is your name and
 * your password.
 */
export const ProfilePage = () => {
  const { user, isLoading, errorKey, reload, apply } = useProfile();
  const { t } = useTranslation();

  if (isLoading && user === null) {
    return (
      <div className={styles.page}>
        <p className={styles.message}>{t('common.loading')}</p>
      </div>
    );
  }

  if (user === null) {
    return (
      <div className={styles.page}>
        <p className={styles.error} role="alert">
          {t(errorKey ?? 'profile.errorLoad')}
        </p>
        <Button onClick={reload}>{t('common.refresh')}</Button>
      </div>
    );
  }

  // Keyed by the account so a reload rebuilds the form from what came back, rather
  // than an effect copying it over what is on screen. A save does NOT change the key
  // — the form keeps what is typed and only rebases what it diffs against.
  return <ProfileForm key={user.id} user={user} onSaved={apply} />;
};

/** The form's fields, all as strings — an input has no other kind of value. */
interface Draft {
  name: string;
  currentPassword: string;
  newPassword: string;
  /** Never sent. It catches a typo here rather than at the next sign-in. */
  confirmPassword: string;
}

const text = (value: string | null | undefined): string => value ?? '';

const draftFrom = (user: UserInfo): Draft => ({
  name: text(user.name),
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

/** Which input a rejected field belongs to. */
const FIELD_OF: Record<string, keyof Draft> = {
  name: 'name',
  currentPassword: 'currentPassword',
  newPassword: 'newPassword',
};

const MIN_PASSWORD = 6;

const ProfileForm = ({ user, onSaved }: { user: UserInfo; onSaved: (user: UserInfo) => void }) => {
  const { t, formatDateTime } = useTranslation();
  const { updateUser, signIn, signOut } = useAuth();

  /** A new session after the password change ended the old one — or the login screen. */
  const signInAgain = async (username: string, password: string) => {
    try {
      const again = await authenticate(username, password);
      if (again.ok) {
        signIn(again.session);
        return;
      }
    } catch {
      // Unreachable server: the same answer as a refusal, below.
    }
    signOut();
  };

  const [draft, setDraft] = useState<Draft>(() => draftFrom(user));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    setFormError(null);
    // The confirmation described a save that no longer matches what is on screen.
    setSaved(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const card = profileCard(draft, user);
    const problems = validate(draft, t);

    if (Object.keys(problems).length > 0) {
      setFieldErrors(problems);
      setFormError(null);
      return;
    }

    // An empty body is a valid request that changes nothing, so it is not sent: a
    // save that writes nothing should say so rather than looking like it worked.
    if (Object.keys(card).length === 0) {
      setFormError(t('profile.nothingChanged'));
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await editMe(card);

      if (result.ok) {
        if (card.newPassword !== undefined) {
          // A new password ends every session on the server, this one included —
          // so the token in hand is already dead. Signed straight back in with the
          // password just set, which brings the updated user along with it; if that
          // fails, the login screen is the honest place to be.
          await signInAgain(user.username, card.newPassword);
        } else {
          // The session's copy of the user is from login and would otherwise keep
          // greeting them by their old name in the header until they signed in
          // again. This is the caller's own account, which is exactly what the
          // session holds — see `updateUser` in app/auth.tsx.
          updateUser(result.user);
        }

        // And the form rebases on it, so what it diffs against is what the server
        // now holds — otherwise every later save would re-send fields that are
        // already correct.
        onSaved(result.user);
        setSaved(true);

        // Emptied on the way out: they are not echoed back, so leaving them filled
        // would mean a second Save posting the same password change again.
        setDraft((current) => ({
          ...current,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
        setShowPassword(false);
        return;
      }

      const { attached, loose } = attachErrors(result.errors, FIELD_OF);
      setFieldErrors(attached);
      setFormError(
        loose.length > 0
          ? loose.join(' ')
          : (result.message ??
              (Object.keys(attached).length > 0 ? null : t('profile.saveFailed'))),
      );
    } catch (caught) {
      // The request itself failed. Reporting it as a rejected field would send the
      // reader to correct something that is fine.
      const status = caught instanceof ApiError ? caught.status : 0;
      setFormError(status === 0 ? t('profile.networkError') : t('profile.serverError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const invalid = (field: keyof Draft) =>
    fieldErrors[field] ? { error: fieldErrors[field] } : {};

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>{t('profile.title')}</h1>
          <p className={styles.lead}>{t('profile.lead')}</p>
        </div>

        <div className={styles.badges}>
          <Badge tone={user.role === 'ADMIN' ? 'accent' : 'neutral'}>
            {user.role ? t(`users.roles.${user.role}`) : t('auth.noRole')}
          </Badge>
        </div>
      </header>

      <form className={styles.card} onSubmit={(event) => void handleSubmit(event)} noValidate>
        {formError && (
          // Announced when it appears: a reader whose focus is still in the field
          // that caused it would otherwise never learn why nothing happened.
          <p className={styles.error} role="alert">
            {formError}
          </p>
        )}

        {saved && (
          <p className={styles.saved} role="status">
            {t('profile.saved')}
          </p>
        )}

        {/*
          The login and the role are shown, never editable — and that is the whole
          reason this endpoint is safe. `UserProfileCard` has no field for either, so
          there is nothing here to disable: the controls simply do not exist. The name
          beside them is the one thing in the account that is yours to change.
        */}
        <Section title={t('profile.account')}>
          <Readout label={t('users.panel.username')} value={user.username} mono />
          <Readout
            label={t('auth.role')}
            value={user.role ? t(`users.roles.${user.role}`) : t('auth.noRole')}
          />
          <Field
            label={t('users.panel.name')}
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
            {...invalid('name')}
            autoComplete="name"
            maxLength={NAME_MAX_LENGTH}
            required
            disabled={isSubmitting}
          />
        </Section>

        {/* <p className={styles.note}>{t('profile.adminOnlyNote')}</p> */}

        <FlightAccessSummary />

        {/*
          The current password is required for a new one, and the server checks it.
          That is not ceremony: it means a session somebody else has taken over cannot
          be used to lock the owner out of their own account.
        */}
        <Section title={t('profile.changePassword')}>
          <Field
            label={t('profile.currentPassword')}
            type={showPassword ? 'text' : 'password'}
            value={draft.currentPassword}
            onChange={(event) => set('currentPassword', event.target.value)}
            {...invalid('currentPassword')}
            autoComplete="current-password"
            disabled={isSubmitting}
            trailing={
              <PasswordReveal shown={showPassword} onToggle={() => setShowPassword((on) => !on)} />
            }
          />
          <Field
            label={t('profile.newPassword')}
            type={showPassword ? 'text' : 'password'}
            value={draft.newPassword}
            onChange={(event) => set('newPassword', event.target.value)}
            hint={t('users.panel.passwordHint', { count: MIN_PASSWORD })}
            {...invalid('newPassword')}
            autoComplete="new-password"
            disabled={isSubmitting}
          />
          <Field
            label={t('profile.confirmPassword')}
            type={showPassword ? 'text' : 'password'}
            value={draft.confirmPassword}
            onChange={(event) => set('confirmPassword', event.target.value)}
            {...invalid('confirmPassword')}
            autoComplete="new-password"
            disabled={isSubmitting}
          />
        </Section>

        <dl className={styles.stamps}>
          {user.created && (
            <div className={styles.stamp}>
              <dt>{t('users.panel.created')}</dt>
              <dd>{formatDateTime(user.created)}</dd>
            </div>
          )}
          {user.updated && (
            <div className={styles.stamp}>
              <dt>{t('users.panel.updated')}</dt>
              <dd>{formatDateTime(user.updated)}</dd>
            </div>
          )}
        </dl>

        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? t('users.panel.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

/**
 * The body: only what actually changed.
 *
 * The endpoint applies exactly the fields it is sent, so this is a diff rather than
 * the whole object.
 *
 * `confirmPassword` is never sent: it exists to catch a typo in this browser, and the
 * server has no field for it.
 */
const profileCard = (draft: Draft, user: UserInfo): UserProfileCard => {
  const card: UserProfileCard = {};

  if (draft.name.trim() !== text(user.name).trim()) card.name = draft.name.trim();

  // Both or neither. The server refuses a new password without the current one, and
  // sending the current one alone would ask it to verify a password and then change
  // nothing.
  if (draft.newPassword !== '') {
    card.currentPassword = draft.currentPassword;
    card.newPassword = draft.newPassword;
  }

  return card;
};

/**
 * The rules that can be settled without asking the server.
 *
 * Whether the current password is CORRECT is the server's to judge, and it answers
 * with a `DataError` on `currentPassword`. Whether one was supplied at all, whether
 * the new one was typed the same way twice, and whether a name is left at all, are
 * not — a name can be changed, never emptied.
 */
const validate = (
  draft: Draft,
  t: (key: Key, values?: Record<string, string | number>) => string,
): Partial<Record<keyof Draft, string>> => {
  const problems: Partial<Record<keyof Draft, string>> = {};

  if (draft.name.trim() === '') problems.name = t('common.required');

  if (draft.newPassword === '') return problems;

  if (draft.currentPassword === '') problems.currentPassword = t('common.required');
  if (draft.newPassword.length < MIN_PASSWORD) {
    problems.newPassword = t('users.panel.passwordTooShort', { count: MIN_PASSWORD });
  }
  if (draft.confirmPassword !== draft.newPassword) {
    problems.confirmPassword = t('profile.passwordMismatch');
  }

  return problems;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div className={styles.grid}>{children}</div>
  </section>
);

/** One stable empty list, so "no codes" is not a new array on every render. */
const NO_CODES: readonly string[] = [];

/**
 * Which flights you see — shown, never editable.
 *
 * An administrator sets it, and the server applies it to every flight screen; this is
 * where a user can read what it is without having to infer it from a board that has
 * fewer rows than a colleague's. It is the access that APPLIES, read once per session
 * from `/user/me/flight-access` — for an administrator always every flight.
 */
const FlightAccessSummary = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { access, hasFailed } = useFlightAccess();
  const names = useAccessNames(access?.airports ?? NO_CODES);

  if (access === null) {
    return (
      <Section title={t('profile.flightAccess')}>
        <Readout
          label={t('profile.flightAccessSees')}
          value={hasFailed ? t('profile.flightAccessFailed') : t('common.loading')}
        />
      </Section>
    );
  }

  const label = (codes: readonly string[], name: (code: string) => string): string =>
    codes.map((code) => (name(code) === code ? code : `${code} — ${name(code)}`)).join(', ');

  return (
    <>
      <Section title={t('profile.flightAccess')}>
        <Readout label={t('profile.flightAccessSees')} value={describeAccess(access, t, names)} />

        {access.restricted && access.airports.length > 0 && (
          <Readout label={t('users.access.airports')} value={label(access.airports, names.airport)} />
        )}
        {access.restricted && access.airlines.length > 0 && (
          <Readout label={t('users.access.airlines')} value={label(access.airlines, names.airline)} />
        )}
      </Section>

      <p className={styles.note}>
        {access.restricted
          ? t('profile.flightAccessRestricted')
          : user?.role === 'ADMIN'
            ? t('profile.flightAccessAdmin')
            : t('profile.flightAccessAll')}
      </p>
    </>
  );
};
